import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { defineMessages, useIntl } from 'react-intl';
import { Dropdown } from 'semantic-ui-react';

import Icon from '@plone/volto/components/theme/Icon/Icon';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import {
  toBackendLang,
  toReactIntlLang,
} from '@plone/volto/helpers/Utils/Utils';
import globeSVG from '@plone/volto/icons/globe.svg';

import { normalizeAppUrl } from '../../../../helpers/appUrl';
import { getLanguageHomePath } from '../../../../helpers/homepageUrl';

const LANGUAGE_LABELS = {
  ca: 'CA',
  en: 'EN',
  es: 'ES',
};

const LANGUAGE_OPTIONS = Object.entries(LANGUAGE_LABELS).map(
  ([value, text]) => ({
    key: value,
    text,
    value,
  }),
);

const messages = defineMessages({
  switchLanguage: {
    id: 'Switch language',
    defaultMessage: 'Switch language',
  },
});

export const getLanguageTarget = (lang, translations) => {
  const translation = translations?.find((item) => item.language === lang);
  if (translation) {
    return normalizeAppUrl(translation['@id']);
  }
  return getLanguageHomePath(lang);
};

const LanguageSelector = ({ onClickAction = () => {} }) => {
  const intl = useIntl();
  const history = useHistory();
  const currentLang = useSelector((state) => state.intl.locale);
  const translations = useSelector(
    (state) => state.content.data?.['@components']?.translations?.items,
  );
  const isMultilingual = useSelector(
    (state) => state.site.data.features?.multilingual,
  );
  const availableLanguages = useSelector(
    (state) => state.site.data?.['plone.available_languages'],
  );

  const options = useMemo(
    () =>
      LANGUAGE_OPTIONS.filter((option) =>
        availableLanguages?.includes(option.value),
      ),
    [availableLanguages],
  );

  const activeLang = toBackendLang(currentLang);
  const selectedLabel = LANGUAGE_LABELS[activeLang] || activeLang.toUpperCase();

  const handleChange = (_event, { value }) => {
    if (!value || value === activeLang) {
      return;
    }
    onClickAction();
    history.push(getLanguageTarget(value, translations));
  };

  if (!isMultilingual) {
    return (
      <Helmet>
        <html lang={toReactIntlLang(currentLang)} />
      </Helmet>
    );
  }

  return (
    <div className="csj-language-dropdown-wrapper">
      <Dropdown
        button
        floating
        icon={null}
        options={options}
        text={selectedLabel}
        value={activeLang}
        aria-label={intl.formatMessage(messages.switchLanguage)}
        onChange={handleChange}
        selectOnBlur={false}
        selectOnNavigation={false}
      />
      <Icon
        name={globeSVG}
        size="14px"
        color="#ccc"
        className="csj-language-globe"
        ariaHidden
      />
    </div>
  );
};

LanguageSelector.propTypes = {
  onClickAction: PropTypes.func,
};

export default LanguageSelector;
